import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Modal,
  IconButton,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useSnackbar } from "notistack";

import Header from "../../../components/Header";
import Menu from "../../../components/Menu";
import Footer from "../../../components/Footer";
import ContentContainer from "../../../components/ContentContainer";
import { UserContext } from "../../../contexts/UserContext";

import "./DarDeBaja.css";

// Importar pdfmake y asignar las fuentes virtuales (vfs)
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts;

dayjs.extend(customParseFormat);

// URL base de tu proxy para búsquedas por RUT
const PROXY_CLIENTES_URL = `${process.env.REACT_APP_API_URL}/proxy/clientes`;

function DarDeBajaPage() {
  const { roleId, token } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const pdfViewerRef = useRef(null);

  // Estados para la búsqueda exacta por RUT
  const [searchRut, setSearchRut] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Si se encuentra un único servicio, lo guardamos en selectedClient.
  // Si se encuentran varios, se almacenan en clientServices para que el usuario seleccione.
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientServices, setClientServices] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const [manualLegalRepresentative, setManualLegalRepresentative] = useState("");

  // Estado para la generación de PDF
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Estado para la vista previa del PDF
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [firmantes, setFirmantes] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState(null);

  // Cargar firmantes disponibles
  useEffect(() => {
    const fetchActiveSigners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/active`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setFirmantes(data.data || []);
      } catch (error) {
        console.error("Error al cargar firmantes:", error);
        enqueueSnackbar("Error al cargar firmantes disponibles", {
          variant: "error",
        });
      }
    };

    fetchActiveSigners();
  }, [token, enqueueSnackbar]);

  useEffect(() => {
    const fetchActiveSigners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signers/active`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setFirmantes(data.data || []);

        // Obtener datos del usuario actual
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (userResponse.ok) {
          const userData = await userResponse.json();

          // Buscar si el usuario actual está en la lista de firmantes
          const currentUserAsSigner = data.data.find(
            (firmante) =>
              firmante.rut === userData.rut || firmante.email === userData.email
          );

          if (currentUserAsSigner) {
            setSelectedSignerId(currentUserAsSigner.id);
          }
        }
      } catch (error) {
        console.error("Error al cargar firmantes:", error);
        enqueueSnackbar("Error al cargar firmantes disponibles", {
          variant: "error",
        });
      }
    };

    fetchActiveSigners();
  }, [token, enqueueSnackbar]);

  // Función auxiliar para obtener el nombre del mes en español
  function obtenerMesEnEspanol(mesNumero) {
    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return meses[mesNumero] || "";
  }

  // Función para formatear fecha con dayjs
  function formatFecha(dateString) {
    if (!dateString) return "________";
    const formatosPosibles = [
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD",
    ];
    let fechaValida = null;
    for (const formato of formatosPosibles) {
      const d = dayjs(dateString, formato, true);
      if (d.isValid()) {
        fechaValida = d;
        break;
      }
    }
    if (!fechaValida) return "________";
    const dia = fechaValida.date();
    const mes = fechaValida.month();
    const anio = fechaValida.year();
    return `${dia} de ${obtenerMesEnEspanol(mes)} de ${anio}`;
  }

  // Función para obtener una imagen en base64 desde /public
  async function getBase64Image(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // --- Búsqueda exacta por RUT ---
  const handleSearchByRut = async () => {
    const term = searchRut.trim();
    if (!term) {
      enqueueSnackbar("Debes ingresar un RUT exacto para la búsqueda.", {
        variant: "warning",
        autoHideDuration: 5000,
        ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
      });
      return;
    }
    setLoadingSearch(true);
    try {
      // Construimos la URL con el query param "cedula" para búsqueda exacta
      const url = new URL(PROXY_CLIENTES_URL);
      url.searchParams.set("cedula", term);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Error al buscar cliente por RUT");
      const result = await response.json();

      if (result.results && result.results.length > 0) {
        if (result.results.length === 1) {
          // Si solo hay un servicio, lo seleccionamos automáticamente
          const c = result.results[0];
          setSelectedClient({
            id: c.id_servicio,
            nombre: c.nombre,
            rut: c.cedula,
            rut_representante_legal: c.cedula,
            folio: c.servicio,
            fechaSuscripcion: c.fecha_instalacion || "",
            correo: c.email || "",
          });
          setClientServices([]);
        } else {
          // Si hay varios servicios, guardamos todos para que el usuario seleccione
          const services = result.results.map((c) => ({
            id: c.id_servicio,
            nombre: c.nombre,
            rut: c.cedula,
            rut_representante_legal: c.cedula,
            folio: c.servicio,
            fechaSuscripcion: c.fecha_instalacion || "",
            correo: c.email || "",
          }));
          setClientServices(services);
          // Aseguramos que no quede seleccionado ninguno previamente
          setSelectedClient(null);
          enqueueSnackbar(
            "Se encontraron varios servicios para este RUT. Por favor, selecciona el servicio a dar de baja.",
            {
              variant: "warning",
              autoHideDuration: 5000,
              ContentProps: {
                sx: { backgroundColor: "#99235c", color: "#fff" },
              },
            }
          );
        }
      } else {
        enqueueSnackbar("No se encontró ningún cliente con ese RUT.", {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        });
        setSelectedClient(null);
        setClientServices([]);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error durante la búsqueda por RUT.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      setSelectedClient(null);
      setClientServices([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  // Datos para PDF y para enviar a Sovos
  const nombreCliente = selectedClient?.nombre || "________";
  const rutCliente = selectedClient?.rut || "________";
  const rutRepresentanteLegal = manualLegalRepresentative || selectedClient?.rut || "________";
  const folioContrato = selectedClient?.folio || "________";
  const correoCliente = manualEmail || selectedClient?.correo || "";
  const fechaSuscripcion = selectedClient?.fechaSuscripcion;
  const fechaMostrada = formatFecha(fechaSuscripcion);

  const hoy = dayjs();
  const fechaHoy = `${hoy.date()} de ${obtenerMesEnEspanol(
    hoy.month()
  )} de ${hoy.year()}`;

  // Crear la definición del PDF
  const createPdfDefinition = async () => {
    const logoBase64 = await getBase64Image("/images/ingbell.png");
    return {
      pageSize: "A4",
      pageMargins: [40, 60, 40, 60],
      content: [
        {
          columns: [
            {
              width: 120,
              stack: [
                {
                  image: `data:image/png;base64,${logoBase64}`,
                  width: 130,
                  alignment: "left",
                  margin: [0, 8, 0, 0],
                },
              ],
              margin: [0, 0, 0, 0],
            },
            {
              width: "*",
              text: "Ingbell Chile SpA",
              style: "header",
              margin: [10, 60, 0, 0],
              alignment: "left",
            },
          ],
          margin: [0, 0, 0, 40],
        },
        {
          text: fechaHoy,
          alignment: "right",
          style: "date",
          margin: [0, 0, 0, 20],
        },
        {
          text: [
            "Conforme establece el artículo 26° del Reglamento de Servicios de Telecomunicaciones, Decreto Supremo N°18 de 2014, a solicitud del cliente ",
            { text: nombreCliente, bold: true },
            ", RUT: ",
            { text: rutCliente, bold: true },
            ", Servicio: ",
            { text: folioContrato, bold: true },
            ", suscrito con fecha ",
            { text: fechaMostrada, bold: true },
            ". Mediante este acto, solicito la baja inmediata de dicho contrato, liberando a ambas partes de las obligaciones correspondientes.",
          ],
          style: "paragraph",
        },
      ],
      styles: {
        header: {
          fontSize: 22,
          bold: true,
          color: "#99235c",
        },
        date: {
          fontSize: 12,
          color: "#555",
        },
        paragraph: {
          fontSize: 12,
          alignment: "justify",
          margin: [0, 0, 0, 10],
          lineHeight: 1.6,
        },
      },
    };
  };
  // --- Generar vista previa del PDF ---
  const handlePreviewPDF = async () => {
    if (!selectedClient) {
      enqueueSnackbar(
        "Primero selecciona un servicio del cliente para visualizar el documento.",
        {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        }
      );
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const docDefinition = await createPdfDefinition();

      // Crear el PDF como blob
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.getBlob((blob) => {
        // Convertir blob a URL
        const url = URL.createObjectURL(blob);
        setPdfPreviewUrl(url);
        setPreviewOpen(true);
        setIsGeneratingPDF(false);
      });
    } catch (error) {
      console.error("Error generando vista previa:", error);
      enqueueSnackbar("Ocurrió un error al generar la vista previa.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      setIsGeneratingPDF(false);
    }
  };

  // Cerrar vista previa
  const handleClosePreview = () => {
    setPreviewOpen(false);
    // Liberar la URL cuando se cierre la vista previa
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  // --- Descargar PDF ---
  const handleDownloadPDF = async () => {
    if (!selectedClient) {
      enqueueSnackbar(
        "Primero selecciona un servicio del cliente para descargar el documento.",
        {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        }
      );
      return;
    }

    try {
      const docDefinition = await createPdfDefinition();
      pdfMake.createPdf(docDefinition).download("baja_contrato.pdf");
    } catch (error) {
      console.error("Error descargando PDF:", error);
      enqueueSnackbar("Ocurrió un error al descargar el documento.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  };

  // --- Enviar PDF a Sovos ---
  const handleEnviarFirmaSovos = async () => {
    if (!selectedClient) {
      enqueueSnackbar(
        "Primero selecciona un servicio del cliente para enviar a firma.",
        {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        }
      );
      return;
    }
    if (!rutCliente || !(manualEmail || selectedClient.correo)) {
      enqueueSnackbar(
        "El RUT y el correo del cliente son obligatorios para enviar a firma.",
        {
          variant: "warning",
          autoHideDuration: 5000,
          ContentProps: { sx: { backgroundColor: "#99235c", color: "#fff" } },
        }
      );
      return;
    }
    setIsGeneratingPDF(true);
    try {
      const docDefinition = await createPdfDefinition();

      pdfMake.createPdf(docDefinition).getBase64((pdfBase64) => {
        const payload = {
          clientRut: rutCliente,
          clientLegalRepresentativeRut: manualLegalRepresentative || selectedClient.rut,
          clientEmail: manualEmail || selectedClient.correo,
          pdfBase64,
          typeCode: "CL2025368836871",
          contractType: "Baja",
          selectedSignerId: selectedSignerId,
        };

        fetch(
          `${process.env.REACT_APP_API_URL}/digital-contracts/send-for-signature`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        )
          .then((res) => res.json())
          .then((data) => {
            if (data.success) {
              enqueueSnackbar("Documento enviado exitosamente para firma.", {
                variant: "success",
                autoHideDuration: 5000,
                ContentProps: {
                  sx: { backgroundColor: "#99235c", color: "#fff" },
                },
              });
            } else {
              enqueueSnackbar(`Error al enviar documento: ${data.message}`, {
                variant: "error",
                autoHideDuration: 5000,
              });
            }
            setIsGeneratingPDF(false);
          })
          .catch((error) => {
            console.error("Error al enviar a firma:", error);
            enqueueSnackbar("Error de conexión con el servidor.", {
              variant: "error",
              autoHideDuration: 5000,
            });
            setIsGeneratingPDF(false);
          });
      });
    } catch (error) {
      console.error("Error al enviar a firma:", error);
      enqueueSnackbar("Error de conexión con el servidor.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Header />
      <div className="content-area">
        <Menu role_id={roleId} className="menu-sidebar" />
        <div className="main-content">
          <ContentContainer>
            {/* Barra para ingresar el RUT exacto */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 4,
                mb: 4,
                maxWidth: 700,
                mx: "auto",
                animation: "fadeIn 0.4s ease",
              }}
            >
              <TextField
                label="Ingresar RUT exacto"
                variant="outlined"
                value={searchRut}
                onChange={(e) => setSearchRut(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchByRut();
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    transition: "all 0.3s ease",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#99235c",
                    },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearchByRut}
                disableElevation
                sx={{
                  backgroundColor: "#99235c",
                  color: "#fff",
                  borderRadius: "10px",
                  fontWeight: 600,
                  px: 4,
                  minWidth: "120px",
                  boxShadow: "0 4px 10px rgba(153, 35, 92, 0.15)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#7a1e49",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 15px rgba(153, 35, 92, 0.2)",
                  },
                }}
              >
                {loadingSearch ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "BUSCAR"
                )}
              </Button>
            </Box>

            {/* Si se encontraron varios servicios para el RUT, mostramos una grilla para seleccionar */}
            {clientServices.length > 1 && (
              <Box
                className="fade-in"
                sx={{
                  mb: 4,
                  maxWidth: 700,
                  mx: "auto",
                  animation: "fadeIn 0.5s ease",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#333", fontWeight: 600 }}
                >
                  Selecciona el servicio a dar de baja
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <RadioGroup
                    value={selectedClient?.id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const service = clientServices.find(
                        (s) => String(s.id) === id
                      );
                      setSelectedClient(service);
                    }}
                  >
                    {clientServices.map((service, index) => (
                      <FormControlLabel
                        key={`${service.folio}-${service.rut}`}
                        value={service.id}
                        control={
                          <Radio
                            sx={{
                              color: "#99235c",
                              "&.Mui-checked": {
                                color: "#99235c",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ py: 0.5 }}>
                            {`${service.folio} - ${service.nombre} (${service.rut})`}
                          </Typography>
                        }
                        sx={{
                          mb: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          animation: `fadeIn 0.4s forwards ${
                            0.2 + index * 0.1
                          }s ease`,
                          "&:hover": {
                            backgroundColor: "rgba(153, 35, 92, 0.04)",
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Paper>
              </Box>
            )}

            {/* Mostrar información del cliente seleccionado con diseño mejorado */}
            {selectedClient && (
              <Box
                className="fade-in"
                sx={{
                  mt: 2,
                  maxWidth: 700,
                  mx: "auto",
                  animation: "fadeIn 0.6s ease",
                }}
              >
                <Paper
                  sx={{
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#f8f8f8",
                      p: 3,
                      borderBottom: "1px solid #eaeaea",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#333",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                      }}
                    >
                      Información del Servicio Seleccionado
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: "1px solid #f0f0f0",
                          pb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            width: "30%",
                            fontWeight: "600",
                            color: "#555",
                          }}
                        >
                          Nombre
                        </Typography>
                        <Typography
                          sx={{ width: "70%", color: "#333", fontWeight: 500 }}
                        >
                          {selectedClient.nombre}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: "1px solid #f0f0f0",
                          pb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            width: "30%",
                            fontWeight: "600",
                            color: "#555",
                          }}
                        >
                          RUT
                        </Typography>
                        <Typography
                          sx={{ width: "70%", color: "#333", fontWeight: 500 }}
                        >
                          {selectedClient.rut}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: "1px solid #f0f0f0",
                          pb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            width: "30%",
                            fontWeight: "600",
                            color: "#555",
                          }}
                        >
                          ID Servicio
                        </Typography>
                        <Typography
                          sx={{ width: "70%", color: "#333", fontWeight: 500 }}
                        >
                          {selectedClient.folio}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          borderBottom: "1px solid #f0f0f0",
                          pb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            width: "30%",
                            fontWeight: "600",
                            color: "#555",
                          }}
                        >
                          Correo
                        </Typography>
                        <Box sx={{ width: "70%" }}>
                          <TextField
                            variant="outlined"
                            size="small"
                            value={manualEmail || selectedClient.correo}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="Agregar o editar correo"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", pb: 1 }}>
                        <Typography
                          sx={{
                            width: "30%",
                            fontWeight: "600",
                            color: "#555",
                          }}
                        >
                          Fecha de Suscripción
                        </Typography>
                        <Typography
                          sx={{ width: "70%", color: "#333", fontWeight: 500 }}
                        >
                          {formatFecha(selectedClient.fechaSuscripcion)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                {/* Firmante y Botones de acción */}
                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    maxWidth: 700,
                    mx: "auto",
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Selector de firmante */}
                  <Box>
                    <Typography
                      sx={{ fontWeight: 600, color: "#555", mb: 1.5 }}
                    >
                      Firmantes del documento
                    </Typography>
                    <FormControl fullWidth variant="outlined" size="small">
                      <TextField
                        variant="outlined"
                        size="small"
                        value={manualLegalRepresentative || selectedClient.rut_representante_legal}
                        onChange={(e) => setManualLegalRepresentative(e.target.value)}
                        placeholder="RUT Firmante Cliente"
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        }}
                      />
                      <FormHelperText>
                        {"RUT Firmante Cliente"}
                      </FormHelperText>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={selectedSignerId || ""}
                        onChange={(e) => setSelectedSignerId(e.target.value)}
                        displayEmpty
                        sx={{
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#99235c",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#99235c",
                          },
                        }}
                      >
                        <MenuItem value="">Firmante por defecto</MenuItem>
                        {firmantes.map((firmante) => (
                          <MenuItem key={firmante.id} value={firmante.id}>
                            {firmante.first_name} {firmante.last_name} (
                            {firmante.rut})
                            {firmante.is_manual ? " (Manual)" : ""}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {selectedSignerId
                          ? "El documento será firmado por el representante seleccionado"
                          : "Se utilizará el firmante por defecto"}
                      </FormHelperText>
                    </FormControl>
                  </Box>

                  {/* Botones de acción */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      justifyContent: "center",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handlePreviewPDF}
                      disabled={isGeneratingPDF}
                      startIcon={<VisibilityIcon />}
                      sx={{
                        color: "#99235c",
                        borderColor: "#99235c",
                        borderRadius: "12px",
                        borderWidth: "1.5px",
                        padding: "12px 20px",
                        "&:hover": {
                          backgroundColor: "rgba(153, 35, 92, 0.04)",
                          borderColor: "#99235c",
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 15px rgba(153, 35, 92, 0.15)",
                        },
                        transition: "all 0.3s ease",
                        fontWeight: "500",
                        flexGrow: 1,
                        flexBasis: 0,
                      }}
                    >
                      Vista Preliminar
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleEnviarFirmaSovos}
                      disabled={isGeneratingPDF}
                      startIcon={<SendIcon />}
                      sx={{
                        backgroundColor: "#99235c",
                        color: "#ffffff",
                        borderRadius: "12px",
                        padding: "12px 20px",
                        boxShadow: "0 4px 10px rgba(153, 35, 92, 0.2)",
                        "&:hover": {
                          backgroundColor: "#7a1e49",
                          boxShadow: "0 6px 15px rgba(153, 35, 92, 0.3)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                        fontWeight: "500",
                        flexGrow: 1,
                        flexBasis: 0,
                      }}
                    >
                      Firmar Documento
                    </Button>
                  </Box>
                </Box>

                {/* PDF Preview Modal */}
                <Modal
                  open={previewOpen}
                  onClose={handleClosePreview}
                  closeAfterTransition
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(5px)",
                  }}
                >
                  <Fade in={previewOpen} timeout={{ enter: 400, exit: 300 }}>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                        p: 2.5,
                        width: "92%",
                        maxWidth: "900px",
                        maxHeight: "92vh",
                        borderRadius: "16px",
                        outline: "none",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "#333" }}
                        >
                          Vista Previa del Documento
                        </Typography>
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={handleDownloadPDF}
                            sx={{
                              mr: 2,
                              color: "#99235c",
                              borderColor: "#99235c",
                              borderRadius: "10px",
                              "&:hover": {
                                borderColor: "#7a1e49",
                                backgroundColor: "rgba(153, 35, 92, 0.04)",
                                transform: "translateY(-2px)",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            Descargar
                          </Button>
                          <IconButton
                            onClick={handleClosePreview}
                            aria-label="cerrar"
                            sx={{
                              color: "#555",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          height: "calc(92vh - 100px)",
                          overflow: "hidden",
                          borderRadius: "12px",
                          border: "1px solid #eaeaea",
                          bgcolor: "#f9f9f9",
                        }}
                      >
                        {pdfPreviewUrl && (
                          <iframe
                            ref={pdfViewerRef}
                            src={pdfPreviewUrl}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                            }}
                            title="Vista previa del PDF"
                          />
                        )}
                      </Box>
                    </Box>
                  </Fade>
                </Modal>
              </Box>
            )}
            {!selectedClient &&
              clientServices.length === 0 &&
              !loadingSearch && (
                <Box
                  sx={{
                    mt: 8,
                    textAlign: "center",
                    color: "#666",
                    maxWidth: 700,
                    mx: "auto",
                    p: 4,
                    borderRadius: "16px",
                    bgcolor: "#fff",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                    opacity: 0,
                    animation: "fadeIn 0.8s forwards ease", // Animación añadida aquí
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Ningún servicio seleccionado
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Ingresa el RUT exacto y presiona "Buscar".
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      animation: "pulse 3s infinite ease-in-out", // Animación para la imagen
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <img
                      src="/images/empty-state.png"
                      alt="Sin servicio"
                      style={{ width: 180, opacity: 0.8 }}
                    />
                  </Box>
                </Box>
              )}
          </ContentContainer>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default DarDeBajaPage;
